// The LIVE world: the deterministic base terrain (engine/chunks.js)
// plus a mutable overlay of edits. One instance per simulation, held
// in ctx.terrain — the same per-sim-state pattern as ctx.pickups and
// ctx.match.
//
// Why an instance and not more module functions: the base is a pure
// function of the seed, identical on server and client, never
// transmitted. Edits are NOT — they're per-room, decided only by the
// server, and sent to clients. A shared module function has nowhere
// to put per-room mutable state; an instance does. The base
// underneath stays shared and immutable (chunks.js caches it by
// seed), so two rooms on one seed share the generation and differ
// only in their thin overlays.
//
// The overlay is two-way and typed: it stores a PACKED TILE per
// edited cell (base material + modifier, or AIR — see
// engine/tiles.js), so an edit can carve a tile away (AIR), fill one
// in, or place a specific material/stack (a wooden door). Edits are
// plain [tcx, tri, packedTile] tuples — trivially serialisable, which
// is exactly what a weapon, a build tool, or a level editor would
// emit. AIR is 0 and the biome base packs to a small integer, so the
// old solid/empty edit format is a subset.

import { generatorFor, floorBounded, DEFAULT_FLOOR, TILE, tileX, tileY } from "./chunks.js";
import { AIR, isSolidTile, materialOf, CUSTOM_TILE } from "./tiles.js";
import { paintIndex } from "./palette.js";
import { SUB, SUBTILE, MAXHP, newCells, present, countPresent, anyPresent, subOf, damageCircle } from "./damage.js";

const key = (tcx, tri) => tcx + "," + tri;

// A tile stops blocking a figure SIDEWAYS once it's mostly gone — below this many of its 36
// sub-cells you can push through it. Vertical support is finer-grained (see surfaceYAt):
// your feet rest on the highest surviving sub-cell under them, so chipping the top of a
// block lowers where you stand a sub-cell at a time rather than all-or-nothing.
const FIGURE_MIN = 10;

const colAt = (x, tcx) => Math.min(SUB - 1, Math.max(0, Math.floor((x - tcx * TILE) / SUBTILE)));

// `gen` names which world generator produces the base (chunks.js GENERATORS); `floor`
// names the world's floor mode (chunks.js FLOORS), whose `bounded` flag decides whether
// the generator stops at the void line or runs down forever. Both ride the `welcome`
// message so client and server build the same world. The generator is a shared instance
// (`generatorFor`, keyed by seed × generator × bounded) held here as `base` — pickups and
// the spawn picker reach it through the terrain, so a room's loot and spawns come from the
// same generator its collision does.
export function createTerrain(seed, gen = "original", floor = DEFAULT_FLOOR) {
    const bounded = floorBounded(floor);
    const base = generatorFor(seed, gen, bounded);

    // tcx,tri -> packed tile. Present only for tiles that have been
    // edited; everything else falls through to the base generation.
    const overlay = new Map();

    // tcx,tri -> 6×6 sub-cell HP grid (see engine/damage.js). Present only for tiles that
    // have been PARTIALLY chipped: a whole tile has no entry, and a tile chipped to nothing
    // is turned to AIR in `overlay` and dropped from here, so damage state stays tiny.
    const damage = new Map();
    const damageCells = (tcx, tri) => damage.get(key(tcx, tri));

    // tcx,tri -> a 6×6 SUB-CELL paint grid (Uint8Array(36); 0 = unpainted, else palette colour + 1).
    // A purely COSMETIC overlay at the same resolution the damage system chips at, so the paint gun's
    // splat is a proper CIRCLE of coloured sub-cells (Set 19.1) rather than a blocky patch of whole
    // tiles — and a tiny droplet can tint a pixel or two rather than missing every tile centre. Changes
    // nothing about solidity/collision (no `rev` bump). Kept parallel to `damage` (not packed into the
    // tile byte) so it carries every colour; syncs/persists as [tcx,tri, 36-int grid] deltas.
    const paint = new Map();
    const paintAt = (tcx, tri) => paint.get(key(tcx, tri));

    // CUSTOM BLOCKS (Set 19, the block maker). Two parallel stores, both server-authoritative and both
    // synced/persisted like the overlay:
    //   `blockDefs`  — id -> a 36-int pixel design (a 6×6 grid of palette colour indices, -1 = uncoloured).
    //                  The DEFINITIONS a player crafts. A placed custom tile just references one by id.
    //   `custom`     — tcx,tri -> the block-def id at that cell. The tile itself wears CUSTOM_TILE (a
    //                  solid placeholder material) so all existing collision/damage works unchanged; the
    //                  renderer looks the id up here and draws the 6×6 design.
    // Kept off the packed tile byte (which has room for neither 36 pixels nor an unbounded id set).
    const blockDefs = new Map();
    const custom = new Map();
    const customAt = (tcx, tri) => custom.get(key(tcx, tri));
    const blockDef = (id) => blockDefs.get(id);

    // Bumped on every mutation (tile edit or sub-tile damage) so the physics colliders know
    // when to rebuild without diffing the whole overlay.
    let rev = 0;

    const tileAt = (tcx, tri) => {
        const o = overlay.get(key(tcx, tri));
        return o === undefined ? base.tileAt(tcx, tri) : o;
    };
    const isSolid = (tcx, tri) => isSolidTile(tileAt(tcx, tri));

    // Record an edit to a packed tile. Returns whether it actually
    // changed the world, so the caller only bothers to broadcast real
    // edits. Setting a tile back to what the base already generates
    // drops the override, keeping the overlay (and the edit set that
    // syncs it) minimal.
    const setTile = (tcx, tri, tile) => {
        if (tileAt(tcx, tri) === tile) return false;
        if (base.tileAt(tcx, tri) === tile) overlay.delete(key(tcx, tri));
        else overlay.set(key(tcx, tri), tile);
        rev++;
        return true;
    };

    return {
        seed,
        gen,
        floor,      // the floor mode name (chunks.js FLOORS)
        bounded,    // whether there's a void line at VOID_Y (else the world runs down forever)
        base,       // the underlying Generator instance (surfaces/structures/spawns/chunkInfo)
        tileAt,
        isSolid,
        setTile,
        damageCells,
        paintAt,
        customAt,
        blockDef,

        // The material NAME at a world point ("water", "grass", …) or null for air — a content-level read
        // (what is this tile made of?) the point-precise solid checks don't answer. Set 19: a plugin
        // enumerating lakes to stock them with fish asks this to tell water from empty air.
        materialAt(x, y) { return materialOf(tileAt(tileX(x), tileY(y))); },

        // Point-precise solidity, for PROJECTILES: is the exact sub-cell under (x,y) still
        // there? A whole tile answers like isSolid; a chipped one lets a shot fly through
        // the hole it (or another) carved. World coordinates in.
        subSolidAt(x, y) {
            const tcx = tileX(x), tri = tileY(y);
            if (!isSolid(tcx, tri)) return false;
            const cells = damage.get(key(tcx, tri));
            if (cells === undefined) return true;             // whole tile
            const { col, row } = subOf(x, y, tcx * TILE, tri * TILE);
            return present(cells, col, row);
        },

        // Coarse SIDE solidity, for figures walking into a wall: a chipped tile keeps
        // blocking until it's mostly gone. Vertical support uses surfaceYAt instead.
        figureSolid(tcx, tri) {
            if (!isSolid(tcx, tri)) return false;
            const cells = damage.get(key(tcx, tri));
            return cells === undefined || countPresent(cells) >= FIGURE_MIN;
        },

        // The walkable surface height at world-x within tile row `tri`: the top of the
        // highest surviving sub-cell in that column, or null if the column's clear there (a
        // foot falls through to the tile below). This is what makes a figure sink a sub-cell
        // at a time as the top of a block is chewed away, instead of dropping a whole tile
        // at once.
        surfaceYAt(x, tri) {
            const tcx = tileX(x);
            if (!isSolid(tcx, tri)) return null;
            const cells = damage.get(key(tcx, tri));
            if (cells === undefined) return tri * TILE;       // whole tile: top is the tile top
            const col = colAt(x, tcx);
            for (let row = 0; row < SUB; row++) if (present(cells, col, row)) return tri * TILE + row * SUBTILE;
            return null;                                      // this column fully carved here
        },

        // Deal `amount` sub-cell damage in a circle at (x,y): weakens every sub-cell within
        // `radius` across the tiles it touches, popping out any that hit 0 and turning a tile
        // to AIR once nothing's left. Returns the deltas to broadcast —
        // { dmg: [[tcx,tri,cells]], edits: [[tcx,tri,tile]] } — so a client replays the exact
        // HP grids (for chipped tiles) and AIR edits (for destroyed ones). Server-authoritative.
        damageAt(x, y, radius, amount) {
            const dmg = [], edits = [];
            const c0 = tileX(x - radius), c1 = tileX(x + radius);
            const r0 = tileY(y - radius), r1 = tileY(y + radius);
            for (let tri = r0; tri <= r1; tri++) {
                for (let tcx = c0; tcx <= c1; tcx++) {
                    if (!isSolid(tcx, tri)) continue;
                    const k = key(tcx, tri);
                    const cells = (damage.get(k) || newCells()).slice();
                    if (!damageCircle(cells, tcx * TILE, tri * TILE, x, y, radius, amount)) continue;
                    if (!anyPresent(cells)) {                 // nothing left — destroy the block
                        damage.delete(k);
                        paint.delete(k);                      // a destroyed tile loses its paint too
                        custom.delete(k);                     // ...and its custom-block design
                        setTile(tcx, tri, AIR);
                        edits.push([tcx, tri, AIR]);
                    } else {
                        damage.set(k, cells);
                        dmg.push([tcx, tri, cells]);
                        rev++;
                    }
                }
            }
            return { dmg, edits };
        },

        // Splash paint over a circle at (x,y): tints every non-air SUB-CELL whose centre falls within
        // `radius` with palette colour `color`, so the splat is a real circle (and a small radius still
        // catches a pixel or two). Purely cosmetic (no HP, no solidity change). Returns the deltas to
        // broadcast — { paints: [[tcx,tri, 36-int grid]] } — which a client replays through applyPaint
        // and the renderer draws. Server-authoritative, like damageAt.
        paintCircle(x, y, radius, color) {
            const c = paintIndex(color) + 1;                  // 0 means unpainted, so store colour + 1
            const paints = [];
            const r2 = radius * radius;
            const c0 = tileX(x - radius), c1 = tileX(x + radius);
            const r0 = tileY(y - radius), r1 = tileY(y + radius);
            for (let tri = r0; tri <= r1; tri++) {
                for (let tcx = c0; tcx <= c1; tcx++) {
                    if (tileAt(tcx, tri) === AIR) continue;   // nothing to paint on empty air
                    const k = key(tcx, tri);
                    const x0 = tcx * TILE, y0 = tri * TILE;
                    let grid = paint.get(k), changed = false;
                    for (let row = 0; row < SUB; row++) {
                        for (let col = 0; col < SUB; col++) {
                            const cx = x0 + col * SUBTILE + SUBTILE / 2, cy = y0 + row * SUBTILE + SUBTILE / 2;
                            if ((cx - x) ** 2 + (cy - y) ** 2 > r2) continue;
                            if (!grid) grid = new Uint8Array(36);
                            const i = row * SUB + col;
                            if (grid[i] !== c) { grid[i] = c; changed = true; }
                        }
                    }
                    if (changed) { paint.set(k, grid); paints.push([tcx, tri, Array.from(grid)]); }
                }
            }
            return { paints };
        },

        // Replay paint deltas from the wire (client side + late-joiner catch-up). A null grid clears a
        // tile's paint, mirroring applyDamage's null-means-whole convention.
        applyPaint(list) {
            for (const [tcx, tri, grid] of list || []) {
                const k = key(tcx, tri);
                if (grid == null) paint.delete(k);
                else paint.set(k, Uint8Array.from(grid));
            }
        },

        // Every painted tile's sub-cell grid, for the welcome handshake + persistence.
        paintEdits() {
            const out = [];
            for (const [k, v] of paint) {
                const c = k.indexOf(",");
                out.push([+k.slice(0, c), +k.slice(c + 1), Array.from(v)]);
            }
            return out;
        },

        // Register/replace a custom block DESIGN (the block maker crafting one, and a reloaded world +
        // a joining client replaying the set). `pixels` is a 36-int array (6×6 palette indices, -1 =
        // uncoloured). Kept as the render/sync source of truth; the block maker plugin keeps the
        // persist authority in its own plugin.state.
        defineBlock(id, pixels) { blockDefs.set(id, pixels); },

        // Place a custom block: a fresh CUSTOM_TILE cell that references design `id`. Like placeBlock
        // (fails on an occupied tile, starts pristine), plus the `custom` id map so the renderer can
        // find the design. Returns the deltas to broadcast, including the `customs` id map.
        placeCustom(tcx, tri, id) {
            if (isSolid(tcx, tri)) return null;
            const k = key(tcx, tri);
            damage.delete(k); paint.delete(k);
            custom.set(k, id);
            setTile(tcx, tri, CUSTOM_TILE);
            // HOLLOW subtiles (Set 20.2): seed a damage grid from the design so uncoloured (-1) pixels are
            // ABSENT (hp 0). The existing sub-cell machinery then treats them as holes for free — physics
            // builds bodies only for present cells, subSolidAt returns false there, and the renderer skips
            // them. A design with no holes stays pristine (no grid → the fast whole-tile path).
            const def = blockDefs.get(id);
            const cells = (def && def.some((p) => p < 0)) ? def.map((p) => (p < 0 ? 0 : MAXHP)) : null;
            if (cells) damage.set(k, cells); else damage.delete(k);
            return { edits: [[tcx, tri, CUSTOM_TILE]], customs: [[tcx, tri, id]], dmg: [[tcx, tri, cells]] };
        },

        // Replay custom-block placements from the wire. A null id clears the mapping (the tile there
        // stopped being a custom block).
        applyCustom(list) {
            for (const [tcx, tri, id] of list || []) {
                const k = key(tcx, tri);
                if (id == null) custom.delete(k); else custom.set(k, id);
            }
        },

        // Every placed custom cell's id, and every known design — for the welcome handshake + persistence.
        customEdits() {
            const out = [];
            for (const [k, v] of custom) { const c = k.indexOf(","); out.push([+k.slice(0, c), +k.slice(c + 1), v]); }
            return out;
        },
        blockDefEdits() {
            const out = [];
            for (const [id, pixels] of blockDefs) out.push([id, pixels]);
            return out;
        },
        applyBlockDefs(list) { for (const [id, pixels] of list || []) blockDefs.set(id, pixels); },

        // Replay damage grids from the wire (client side, and late-joiner catch-up). A null
        // grid means "this tile is whole again" (a block was placed/broken there).
        applyDamage(list) {
            for (const [tcx, tri, cells] of list || []) {
                const k = key(tcx, tri);
                if (!cells) damage.delete(k);
                else damage.set(k, cells);
                rev++;
            }
        },

        // Place a fresh whole block (the trowel). `tile` is the packed material to lay
        // down; omit it to use the generator's local fill. No-op (returns null) if the tile
        // is already solid. A newly-placed block starts pristine, so any leftover damage is
        // cleared. Returns the deltas to broadcast (dmg null = "whole again").
        placeBlock(tcx, tri, tile) {
            if (isSolid(tcx, tri)) return null;
            damage.delete(key(tcx, tri));
            paint.delete(key(tcx, tri));   // a fresh block starts unpainted
            setTile(tcx, tri, tile ?? base.fillTileAt(tcx, tri));
            return { edits: [[tcx, tri, tileAt(tcx, tri)]], dmg: [[tcx, tri, null]] };
        },

        // Break a whole block (the mallet). No-op (returns null) if the tile isn't solid.
        // Drops any sub-tile damage, since the tile is gone. `tile` in the return is the
        // material that was there, so the breaker can pocket that exact block type.
        removeBlock(tcx, tri) {
            if (!isSolid(tcx, tri)) return null;
            const was = tileAt(tcx, tri);
            damage.delete(key(tcx, tri));
            paint.delete(key(tcx, tri));   // the tile's gone; its paint goes with it
            custom.delete(key(tcx, tri));  // ...and any custom-block design it carried
            setTile(tcx, tri, AIR);
            return { tile: was, edits: [[tcx, tri, AIR]], dmg: [[tcx, tri, null]] };
        },

        // Every partially-chipped tile's mask, for the welcome handshake.
        damageEdits() {
            const out = [];
            for (const [k, v] of damage) {
                const c = k.indexOf(",");
                out.push([+k.slice(0, c), +k.slice(c + 1), v]);
            }
            return out;
        },

        // Convenience for the solid/empty case: fill with the generator's LOCAL base
        // material (so building matches the terrain around it, and re-solidifying an
        // already-solid tile is a genuine no-op) or carve to air. The typed setTile is
        // the general form.
        setSolid(tcx, tri, solid) { return setTile(tcx, tri, solid ? base.fillTileAt(tcx, tri) : AIR); },

        // The full current edit set, as [tcx, tri, packedTile] tuples
        // — what a joining client needs to reconstruct the mutated
        // world before its first frame.
        edits() {
            const out = [];
            for (const [k, v] of overlay) {
                const c = k.indexOf(",");
                out.push([+k.slice(0, c), +k.slice(c + 1), v]);
            }
            return out;
        },

        // Apply a batch of edits (from the wire). Authoritative:
        // takes the value as given. Keeps the overlay minimal the
        // same way setTile does.
        applyEdits(list) {
            for (const [tcx, tri, tile] of list || []) {
                if (base.tileAt(tcx, tri) === tile) overlay.delete(key(tcx, tri));
                else overlay.set(key(tcx, tri), tile);
                rev++;
            }
        },

        clear() { overlay.clear(); damage.clear(); paint.clear(); custom.clear(); blockDefs.clear(); rev++; },
        get editCount() { return overlay.size; },
        get damageCount() { return damage.size; },
        get rev() { return rev; }, // mutation counter — physics colliders rebuild when it changes
    };
}
